import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './SettingsPartner.css';

const SettingsPartner = () => {
  const [partner, setPartner]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const [activeTab, setActiveTab]   = useState('profile');
  const timerRef                    = useRef(null);

  const [profile, setProfile] = useState({
    first_name:   '',
    last_name:    '',
    email:        '',
    phone:        '',
    company_name: '',
    country_code: 'FR'
  });

  const [passwords, setPasswords] = useState({
    current_password:  '',
    new_password:      '',
    confirm_password:  ''
  });

  useEffect(() => {
    fetchProfile();
    return () => clearTimeout(timerRef.current);
  }, []);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMsg(null), 4000);
  };

  const fetchProfile = async () => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const user = JSON.parse(stored);
        setPartner(user);
        setProfile({
          first_name:   user.first_name   || '',
          last_name:    user.last_name    || '',
          email:        user.email        || '',
          phone:        user.phone        || '',
          company_name: user.company_name || '',
          country_code: user.country_code || 'FR'
        });
      }
    } catch (err) {
      console.error('Erreur chargement profil:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile.first_name || !profile.last_name || !profile.email) {
      showMsg('error', 'Prénom, nom et email sont requis');
      return;
    }

    setSaving(true);
    try {
      await API.put('/auth/partner/profile', profile);
      // Met à jour le localStorage
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, ...profile }));
      showMsg('success', '✅ Profil mis à jour avec succès');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current_password || !passwords.new_password) {
      showMsg('error', 'Tous les champs mot de passe sont requis');
      return;
    }
    if (passwords.new_password.length < 8) {
      showMsg('error', 'Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      showMsg('error', 'Les mots de passe ne correspondent pas');
      return;
    }

    setSaving(true);
    try {
      await API.put('/auth/partner/password', {
        current_password: passwords.current_password,
        new_password:     passwords.new_password
      });
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
      showMsg('success', '✅ Mot de passe modifié avec succès');
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Mot de passe actuel incorrect');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="loader-pro"></div>
        <p>Chargement des paramètres...</p>
      </div>
    );
  }

  return (
    <div className="settings-partner-pro">

      {/* ── Hero ── */}
      <motion.div
        className="settings-header-pro"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <div className="sett-hero-orb sett-hero-orb-1" />
        <div className="sett-hero-orb sett-hero-orb-2" />
        <div className="settings-header-left">
          <div className="settings-header-icon">
            <i className="fas fa-cog"></i>
          </div>
          <div>
            <h1>Paramètres</h1>
            <p>Gérez votre profil et la sécurité de votre compte</p>
          </div>
        </div>
        <div style={{ zIndex: 1 }}>
          <div className="sett-stat-pill">
            <i className="fas fa-user" />
            <span>{partner?.first_name} {partner?.last_name}</span>
          </div>
        </div>
      </motion.div>

      {/* Messages */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`alert-pro ${msg.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="settings-tabs-pro">
        {[
          { id: 'profile',  label: 'Profil',   icon: 'fa-user' },
          { id: 'security', label: 'Sécurité', icon: 'fa-lock' },
          { id: 'account',  label: 'Compte',   icon: 'fa-building' }
        ].map(tab => (
          <motion.button
            key={tab.id}
            className={`settings-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <i className={`fas ${tab.icon}`}></i>
            {tab.label}
          </motion.button>
        ))}
      </div>

      {/* Contenu */}
      <AnimatePresence mode="wait">

        {/* ── ONGLET PROFIL ── */}
        {activeTab === 'profile' && (
          <motion.div
            key="profile"
            className="settings-card-pro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <h3><i className="fas fa-user"></i> Informations personnelles</h3>

            <div className="settings-form-grid">
              <div className="form-group-pro">
                <label>Prénom</label>
                <input
                  type="text"
                  value={profile.first_name}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder="Votre prénom"
                />
              </div>
              <div className="form-group-pro">
                <label>Nom</label>
                <input
                  type="text"
                  value={profile.last_name}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder="Votre nom"
                />
              </div>
              <div className="form-group-pro">
                <label>Email</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={e => setProfile({ ...profile, email: e.target.value })}
                  placeholder="votre@email.com"
                />
              </div>
              <div className="form-group-pro">
                <label>Téléphone</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={e => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+33 6 00 00 00 00"
                />
              </div>
              <div className="form-group-pro">
                <label>Pays</label>
                <select
                  value={profile.country_code}
                  onChange={e => setProfile({ ...profile, country_code: e.target.value })}
                >
                  <option value="FR">France</option>
                  <option value="TN">Tunisie</option>
                  <option value="MA">Maroc</option>
                  <option value="DZ">Algérie</option>
                  <option value="BE">Belgique</option>
                </select>
              </div>
            </div>

            <div className="settings-footer-pro">
              <motion.button
                className="btn-save-pro"
                onClick={handleSaveProfile}
                disabled={saving}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Enregistrement...</> 
                        : <><i className="fas fa-check"></i> Enregistrer</>}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── ONGLET SÉCURITÉ ── */}
        {activeTab === 'security' && (
          <motion.div
            key="security"
            className="settings-card-pro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <h3><i className="fas fa-lock"></i> Changer le mot de passe</h3>

            <div className="settings-form-grid single">
              <div className="form-group-pro">
                <label>Mot de passe actuel</label>
                <input
                  type="password"
                  value={passwords.current_password}
                  onChange={e => setPasswords({ ...passwords, current_password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="form-group-pro">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwords.new_password}
                  onChange={e => setPasswords({ ...passwords, new_password: e.target.value })}
                  placeholder="Min. 8 caractères"
                />
              </div>
              <div className="form-group-pro">
                <label>Confirmer le nouveau mot de passe</label>
                <input
                  type="password"
                  value={passwords.confirm_password}
                  onChange={e => setPasswords({ ...passwords, confirm_password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="info-banner">
              <i className="fas fa-shield-alt"></i>
              <span>Utilisez au moins 8 caractères avec des lettres et des chiffres</span>
            </div>

            <div className="settings-footer-pro">
              <motion.button
                className="btn-save-pro"
                onClick={handleChangePassword}
                disabled={saving}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Modification...</>
                        : <><i className="fas fa-lock"></i> Changer le mot de passe</>}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── ONGLET COMPTE ── */}
        {activeTab === 'account' && (
          <motion.div
            key="account"
            className="settings-card-pro"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <h3><i className="fas fa-building"></i> Informations entreprise</h3>

            <div className="settings-form-grid single">
              <div className="form-group-pro">
                <label>Nom de l'entreprise</label>
                <input
                  type="text"
                  value={profile.company_name}
                  onChange={e => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="Nom de votre entreprise"
                />
              </div>
            </div>

            {/* Infos compte non modifiables */}
            <div className="account-info-grid">
              <div className="account-info-item">
                <span className="info-label">Statut du compte</span>
                <span className="badge-pro badge-success">
                  <i className="fas fa-check-circle"></i> Actif
                </span>
              </div>
              <div className="account-info-item">
                <span className="info-label">Type de compte</span>
                <span className="info-value">Partenaire</span>
              </div>
              <div className="account-info-item">
                <span className="info-label">Membre depuis</span>
                <span className="info-value">
                  {partner?.created_at
                    ? new Date(partner.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })
                    : '—'}
                </span>
              </div>
            </div>

            <div className="settings-footer-pro">
              <motion.button
                className="btn-save-pro"
                onClick={handleSaveProfile}
                disabled={saving}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {saving ? <><i className="fas fa-spinner fa-spin"></i> Enregistrement...</>
                        : <><i className="fas fa-check"></i> Enregistrer</>}
              </motion.button>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default SettingsPartner;